
import { ContentItem, Video } from 'interfaces'
import { EVENT_QUEUE_POSTS } from 'routes/trial/interfaces'
import { Observable, of as observableOf } from 'rxjs'
import { tap } from 'rxjs/operators'
import { getBuckets } from 'services/post/actions'
import { withPostTypeConstraints } from 'services/compose/tiktok/utils'
import { trackTrainingEvent } from 'services/users/actions'
import {
  BRAND_BLUESKY,
  BRAND_THREADS,
  BRAND_TIKTOK,
  BRAND_YOUTUBE,
  STOCK_VIDEO_TYPE,
  VIDEO_TYPE,
  BRAND_FACEBOOK,
  BRAND_INSTAGRAM,
  BRAND_LINKEDIN,
  BRAND_PINTEREST,
  BRAND_TWITTER,
  BRAND_GOOGLE
} from 'shared'
import {
  ComposerBlueskyPost,
  ComposerFacebookPost,
  ComposerGooglePost,
  ComposerInstagramPost,
  ComposerLinkedinPost,
  ComposerPinterestPost,
  ComposerThreadsPost,
  ComposerTikTokPost,
  ComposerTwitterPost,
  ComposerYoutubePost,
  CreatePostRequestData,
  GOOGLE_POST_TYPE_EVENT,
  GOOGLE_POST_TYPE_OFFER,
  GOOGLE_POST_TYPE_UPDATE
} from 'shared/types'
import { getState, StoreThunkAction, StoreThunkDispatch } from 'store/state'
import {
  generateBulkPostCaptionFromHtml,
  getFBPostTextFromHtml,
  getLinkedinFormattedText,
  getTagsInfoFromHtml,
  getTextFromHtml
} from 'utils/composer'
import {
  composerBulkPostsSelector,
  composerSelectedBucketIdSelector,
  composerSelectedProfilesSelector,
  composerTabsSelector,
  genericPostSelector,
  useGenericStatusSelector
} from '../selectors'
import { BulkContentPost } from '../state'
import { toPinterestAiContentMode } from '../utils'
import { V1 } from './net'

export function getMultiPostsData(shareType: 'queue' | 'next'): StoreThunkAction<CreatePostRequestData[]> {
  return (_dispatch: StoreThunkDispatch, getState: getState) => {
    const state = getState()
    const networks = composerTabsSelector(state)
    const allProfiles = composerSelectedProfilesSelector(state)
    const allPosts = composerBulkPostsSelector(state)
    const genericStatus = genericPostSelector(state).status
    const posts = allPosts.filter(post => networks.includes(post.network))
    const useGenericStatus = useGenericStatusSelector(state)

    return posts.map((post: BulkContentPost) => {
      const recycle = post.recycle
      const link = post.article
      const featuredImageUrl = post.imageUrl || post.content?.imageUrl
      const pinterestBoards = post.network === BRAND_PINTEREST ? (post.data as ComposerPinterestPost).boards || [] : []
      const ppPageIds: number[] = []
      const { html: genericStatusHtml } = generateBulkPostCaptionFromHtml(genericStatus.html, post)
      const statusText = useGenericStatus ? getTextFromHtml(genericStatusHtml) : null

      allProfiles.forEach(profile => {
        if (profile.type === post.network) {
          ppPageIds.push(parseInt(profile.ppid, 10))
        }
      })

      const video = post.content?.type === VIDEO_TYPE || post.content?.type === STOCK_VIDEO_TYPE ? post.content : null

      const data: CreatePostRequestData = {
        postType: video ? 'video_link' : featuredImageUrl ? 'photo' : 'link',
        ppPageIds,
        networks: {},
        frontendId: post.id,
        shareType
      }

      if (recycle) {
        data.recycle = true
        if (typeof recycle === 'object') {
          data.recycleUntil = recycle.toISOString()
        } else if (typeof recycle === 'number') {
          data.recycleTimes = recycle
        }
      }

      if (featuredImageUrl && !post.videoUrl) {
        data.photos = [featuredImageUrl]
      }

      if (link) {
        data.link = {
          title: link.title,
          url: link.url,
          description: link.description
        }
        data.postType = 'link'
        data.photos = [link.imageUrl]
      }

      if (video && !post.videoUrl) {
        data.link = {
          title: (video as Video).title,
          description: (video as Video).description,
          url: video.socialLink
        }
      }

      if (post.videoUrl) {
        data.videoUrl = post.videoUrl
        data.postType = 'video'
      }

      if (post.content && !post.videoUrl) {
        let date = (post.content as ContentItem).createdAt
        if (typeof date === 'number' || typeof date === 'string') {
          date = new Date(date).toISOString()
          data.sourcePost = { date }
        } else {
          data.sourcePost = { date: date.toISOString() }
        }
        data.sourcePost.feedName = post.content.feed.name
        data.sourcePost.feedType = post.content.feed.type
        data.sourcePost.socialLink = post.content.socialLink
        data.sourcePost.sourceLink = (post.content as any).sourceLink || post.content.socialLink
      }

      switch (post.network) {
        case BRAND_FACEBOOK: {
          const status = statusText || getFBPostTextFromHtml(post.data.status.html)
          const tags = getTagsInfoFromHtml(post.data.status.html)
          const facebookPost: any = { status, details: {} }
          const fbComposerPost = post.data as ComposerFacebookPost
          const postType = fbComposerPost.postType
          const selectedAlbums = fbComposerPost.albums || []
          const comments = Array.isArray(fbComposerPost.comments) ? fbComposerPost.comments : []
          const formattedComments = comments
            .filter(comment => comment.text?.trim())
            .map(comment => ({
              text: getFBPostTextFromHtml(comment.html).trim(),
              tags: getTagsInfoFromHtml(comment.html),
              delay: comment.delay || 0
            }))

          if (formattedComments.length > 0) {
            facebookPost.comments = formattedComments
          }

          if (postType) {
            facebookPost.details.postType = postType
          }
          if (tags.length > 0) {
            facebookPost.details.tags = tags
          }
          if (selectedAlbums.length > 0) {
            facebookPost.details.albums = selectedAlbums.map(a => ({ ...a, ppPageId: a.ppid }))
          }

          // EXPL: always sent - `composer_new` replaces the whole post, so an omitted key un-flags it on edit
          facebookPost.details.isAiContent = Boolean(post.isAiContent)

          if (fbComposerPost.reshare) {
            facebookPost.reshare = true
            if (post.content?.feed.type === BRAND_FACEBOOK) {
              facebookPost.details.reshareUrl = post.content.socialLink
              facebookPost.details.reshareName = post.content.feed.name
            }
          }

          data.networks.facebook = facebookPost
          break
        }

        case BRAND_TWITTER: {
          const status = statusText || post.data.status.text
          const twTags = getTagsInfoFromHtml(post.data.status.html)
          const twitterPost: any = {
            status,
            details: {
              tweetType: (post.data as ComposerTwitterPost).tweetType
            }
          }
          const comments = (post.data as ComposerTwitterPost).comments
          const formattedComments = comments.filter(comment => comment.text?.trim())

          if (formattedComments.length > 0) {
            twitterPost.comments = formattedComments
          }

          if (twTags.length > 0) {
            twitterPost.details.tags = twTags
          }
          // EXPL: always sent - `composer_new` replaces the whole post, so an omitted key un-flags it on edit
          twitterPost.details.isAiContent = Boolean(post.isAiContent)
          if ((post.content as ContentItem)?.feed.type === BRAND_TWITTER) {
            twitterPost.details.tweetUrl = (post.content as ContentItem).socialLink
            data.sourcePost = data.sourcePost || {}
            data.sourcePost.feedName = (post.content as ContentItem).feed.name
            data.sourcePost.text = (post.content as ContentItem).tweetText
          }
          data.networks.twitter = twitterPost
          break
        }

        case BRAND_BLUESKY: {
          const { facets, comments, status } = post.data as ComposerBlueskyPost
          const tags = getTagsInfoFromHtml(status.html)
          const bsComments = comments.filter(comment => comment.text?.trim())

          data.networks.bluesky = {
            status: statusText || status.text,
            details: {}
          }

          if (bsComments.length > 0) {
            data.networks.bluesky.comments = bsComments
          }

          if (facets) {
            data.networks.bluesky.details!.facets = facets
          }
          if (tags.length > 0) {
            data.networks.bluesky.details!.tags = tags
          }
          break
        }

        case BRAND_INSTAGRAM: {
          const instagramPost: any = {
            status: statusText || post.data.status.text,
            details: {}
          }
          const { locations, comments, postType, coverUrl } = post.data as ComposerInstagramPost
          const locationsArray = Object.keys(locations || {}).reduce((arr: any[], ppid) => {
            if (locations[ppid]) {
              arr.push({ ...locations[ppid], ppPageId: ppid })
            }
            return arr
          }, [])
          const igComments = comments.filter(comment => comment.text?.trim())

          if (postType) {
            instagramPost.details.postType = postType
          }

          if (locationsArray.length > 0) {
            instagramPost.details.locations = locationsArray
          }

          if (coverUrl && post.videoUrl) {
            instagramPost.details.coverUrl = coverUrl
            data.videoThumbnailUrl = coverUrl
          }

          if (igComments.length > 0) {
            instagramPost.comments = comments
          }

          // EXPL: always sent - `composer_new` replaces the whole post, so an omitted key un-flags it on edit
          instagramPost.details.isAiContent = Boolean(post.isAiContent)

          data.networks.instagram = instagramPost
          break
        }

        case BRAND_PINTEREST: {
          const pin = post.data as ComposerPinterestPost
          const pinterestPost: any = {
            status: pin.title,
            description: statusText || pin.description || '',
            destinationUrl: pin.destinationUrl,
            details: { boards: [] }
          }
          pinterestPost.piAiContentMode = toPinterestAiContentMode(Boolean(post.isAiContent), pin.aiContentMode)
          // EXPL: always sent - `composer_new` replaces the whole post, so an omitted key un-flags it on edit.
          // Any mode flags the pin - an AI-generated person is AI content even with the row flag off.
          pinterestPost.details.isAiContent = pinterestPost.piAiContentMode !== null
          if (pinterestBoards.length > 0) {
            pinterestPost.details.boards = pinterestBoards.map(b => ({ ...b, ppPageId: b.ppid }))
          }
          data.networks.pinterest = pinterestPost
          break
        }

        case BRAND_YOUTUBE: {
          data.networks.youtube = {
            status: (post.data as ComposerYoutubePost).title,
            description: statusText || (post.data as ComposerYoutubePost).description || '',
            details: {
              privacyStatus: (post.data as ComposerYoutubePost).privacyStatus,
              // EXPL: always sent - `composer_new` replaces the whole post, so an omitted key un-flags it on edit
              isAiContent: Boolean(post.isAiContent)
            }
          }
          const comments = (post.data as ComposerYoutubePost).comments.filter(comment => comment.text?.trim())
          if (comments.length > 0) {
            data.networks.youtube.comments = comments
          }
          break
        }

        case BRAND_LINKEDIN: {
          const { tags, text } = getLinkedinFormattedText(post.data.status.html)

          const linkedInPost: any = {
            status: statusText || post.data.status.text,
            details: { tags, text }
          }
          data.networks.linkedin = linkedInPost

          const comments = (post.data as ComposerLinkedinPost).comments.filter(comment => comment.text?.trim())
          if (comments.length > 0) {
            linkedInPost.comments = comments
          }
          break
        }

        case BRAND_THREADS: {
          const comments = (post.data as ComposerThreadsPost).comments.filter(comment => comment.text?.trim())
          data.networks.threads = { status: statusText || post.data.status.text }
          if (comments.length > 0) {
            data.networks.threads.comments = comments
          }
          break
        }

        case BRAND_GOOGLE: {
          const status = statusText || post.data.status.text
          const gPost = post.data as ComposerGooglePost
          const googlePost: any = {
            status,
            details: {
              postType: gPost.selectedPostType
            }
          }

          switch (gPost.selectedPostType) {
            case GOOGLE_POST_TYPE_UPDATE:
              googlePost.details.buttonType = gPost.update.buttonType
              googlePost.details.buttonUrl = gPost.update.link
              break

            case GOOGLE_POST_TYPE_OFFER:
              googlePost.details.couponCode = gPost.offer.offer.couponCode
              googlePost.details.redeemOnlineUrl = gPost.offer.offer.redeemOnlineUrl
              googlePost.details.termsConditions = gPost.offer.offer.termsConditions
              googlePost.details.title = gPost.offer.event.title
              googlePost.details.startDate = gPost.offer.event.schedule.startDate
              googlePost.details.endDate = gPost.offer.event.schedule.endDate
              break

            case GOOGLE_POST_TYPE_EVENT:
              googlePost.details.title = gPost.event.title
              googlePost.details.startDate = gPost.event.schedule.startDate
              googlePost.details.endDate = gPost.event.schedule.endDate
              break
          }

          data.networks.google = googlePost
          break
        }

        case BRAND_TIKTOK: {
          // Duet and Stitch are video-only. The settings can still carry them - the popup that strips them is mounted
          // lazily and may never have been opened - so enforce it here, where the post type is known for certain.
          // The row's AI content checkbox owns the flag - whatever the profile settings carry is overridden.
          const tkSettings = Object.values((post.data as ComposerTikTokPost).settings)
            .map(profileSettings => withPostTypeConstraints(profileSettings, !video && !post.videoUrl))
            .map(profileSettings => ({ ...profileSettings, isAiContent: Boolean(post.isAiContent) }))
          data.networks.tiktok = {
            status: statusText || post.data.status.text,
            details: {
              settings: tkSettings
            }
          }
          if (featuredImageUrl) {
            data.networks.tiktok.title = (post.data as ComposerTikTokPost).title
            data.networks.tiktok.description = (post.data as ComposerTikTokPost).description
          }
          break
        }
      }
      return data
    })
  }
}

export function createMultiPost(shareType: 'queue' | 'next', onBeforeCreate?: () => void): StoreThunkAction<Observable<any>> {
  return (dispatch, getState) => {
    const state = getState()
    try {
      const data = dispatch(getMultiPostsData(shareType))
      const selectedBucketId = composerSelectedBucketIdSelector(state)

      if (onBeforeCreate) {
        onBeforeCreate()
      }
      return dispatch(V1.createPost(data, selectedBucketId)).pipe(tap((response: any) => {
        dispatch(trackTrainingEvent({ eventCode: EVENT_QUEUE_POSTS, value: response.count }))
        if (selectedBucketId) {
          dispatch(getBuckets(true))
        }
      }))
    } catch (error) {
      return observableOf({ error })
    }
  }
}
